import React, { useState } from 'react';
import { Project } from '../types';
import { Tag, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { ProjectModal } from './ProjectModal';

interface ProjectTableProps {
  projects: Project[];
  onProjectUpdate: () => void;
}

interface CollapsibleTagsProps {
  tags: { id: string; name: string }[];
}

interface CollapsibleTeamProps {
  staff: { id: string; name: string }[];
}

function CollapsibleTags({ tags }: CollapsibleTagsProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const validTags = tags.filter(tag => tag && tag.id && tag.name);
  const visibleTags = isExpanded ? validTags : validTags.slice(0, 3);
  const remainingCount = validTags.length - 3;

  if (validTags.length <= 3) {
    return (
      <div className="flex flex-wrap gap-2">
        {validTags.map((tag) => (
          <span
            key={tag.id}
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
          >
            <Tag className="w-3 h-3 mr-1" />
            {tag.name}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {visibleTags.map((tag) => (
          <span
            key={tag.id}
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
          >
            <Tag className="w-3 h-3 mr-1" />
            {tag.name}
          </span>
        ))}
        {!isExpanded && remainingCount > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(true);
            }}
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
          >
            <ChevronDown className="w-3 h-3 mr-1" />
            +{remainingCount} more
          </button>
        )}
        {isExpanded && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(false);
            }}
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
          >
            <ChevronUp className="w-3 h-3 mr-1" />
            Show less
          </button>
        )}
      </div>
    </div>
  );
}

function CollapsibleTeam({ staff }: CollapsibleTeamProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const validStaff = staff.filter(member => member && member.id && member.name);
  const visibleStaff = isExpanded ? validStaff : validStaff.slice(0, 3);
  const remainingCount = validStaff.length - 3;

  if (validStaff.length <= 3) {
    return (
      <div className="flex flex-wrap gap-2">
        {validStaff.map((member) => (
          <span
            key={member.id}
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
          >
            <Users className="w-3 h-3 mr-1" />
            {member.name}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {visibleStaff.map((member) => (
          <span
            key={member.id}
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
          >
            <Users className="w-3 h-3 mr-1" />
            {member.name}
          </span>
        ))}
        {!isExpanded && remainingCount > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(true);
            }}
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
          >
            <ChevronDown className="w-3 h-3 mr-1" />
            +{remainingCount} more
          </button>
        )}
        {isExpanded && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(false);
            }}
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
          >
            <ChevronUp className="w-3 h-3 mr-1" />
            Show less
          </button>
        )}
      </div>
    </div>
  );
}

export function ProjectTable({ projects, onProjectUpdate }: ProjectTableProps) {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const handleProjectUpdate = (updatedProject: Project) => {
    onProjectUpdate();
    setSelectedProject(null);
  };

  return (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Project Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Customer Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Year
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tags
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Team
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {projects.map((project) => (
              <tr
                key={project.id}
                onClick={() => setSelectedProject(project)}
                className="hover:bg-gray-50 cursor-pointer"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {project.projectName}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {project.customer.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {project.year || '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <CollapsibleTags tags={project.tags} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <CollapsibleTeam staff={project.staff} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedProject && (
        <ProjectModal
          project={selectedProject}
          onClose={() => setSelectedProject(null)}
          onUpdate={handleProjectUpdate}
        />
      )}
    </>
  );
}